<!--
  PoolaPage — the member Poola buy-screen STATE HOST.
  Standalone route /poola/ + /suppliers/poola/ (requireAuth, NOT
  requireMembership). Reachable by active sellers, active suppliers, and
  pending users — identical shell for all audiences.

  This page owns ONLY the page shell (<f7-page> + <f7-navbar> + PTR
  <f7-page-content>) and usePoolaDeal()'s state machine. It derives ONE state
  and renders ONE child. Each state child is a CONTENT FRAGMENT — never its own
  <f7-page>; nesting a page here would trip the F7 page-nesting trap (the active
  fragment clips to ~50px, playbook Section 5 row 17). Do not wrap the host in an
  extra <f7-page> either.

  This note sits ABOVE <template> on purpose. In dev Vue keeps template comments,
  so a comment next to <f7-page> makes the component a FRAGMENT. When this page
  is rebuilt by a back navigation (from the last-Poola page), Framework7 moves
  its element in front of the page being left — out of the fragment's range —
  and the next push then removes only the fragment's markers: the page's element
  stays in the view as a dead `.page-previous` shell. Keep <f7-page> the ONE root
  node of the template.

  `:page-content="false"` on <f7-page>: the page brings its OWN <f7-page-content>
  (for pull-to-refresh). Without the prop <f7-page> wraps that one in a second
  .page-content and both get the navbar's padding, so everything starts one
  navbar-height too low (a blank band under the navbar; measured 76px).
-->
<template>
  <f7-page name="poola" :page-content="false">
    <!-- `back-link-show-text` is required for the «رجوع» label to render at
         all: F7's nav-left drops the label unless it is set, leaving a bare
         chevron (nav-left.js — `backLink !== true && backLinkShowText`). -->
    <f7-navbar
      :title="$t('tabs.poola')"
      :back-link="$t('general.back')"
      back-link-show-text
    ></f7-navbar>

    <!-- The supplier shell's bar, on /suppliers/poola/ only. Before the
         page-content so F7's sibling rule pads the content above it. -->
    <supplier-tabbar-navigation
      v-if="showSupplierBar"
      standalone
    ></supplier-tabbar-navigation>

    <f7-page-content ptr ptr-mousewheel @ptr:refresh="handlePullToRefresh">
      <!-- One derived state → one child. -->
      <poola-skeleton v-if="state === POOLA_STATE.LOADING"></poola-skeleton>

      <poola-error v-else-if="state === POOLA_STATE.ERROR" @retry="refresh"></poola-error>

      <poola-pool-hero v-else-if="state === POOLA_STATE.EMPTY" mode="empty"></poola-pool-hero>

      <!--
        Distinct :key per phase forces a remount on a status flip (coming→live,
        live→ended), so usePoolaOffers re-fetches with the now-correct canClaim
        (the run unlocks/locks). A brief skeleton on flip is acceptable (plan Section 1).
        @deal-not-active → refetch the singleton: a claim found the run flipped.

        Viewer branch: an activated supplier gets the READ-ONLY PoolaSupplierRun
        (no buy path); a prospect (non-member) gets the READ-ONLY PoolaProspectRun
        (no buy path + sticky join CTA); everyone else gets the seller PoolaRun
        exactly as before. The supplier + prospect variants are checked FIRST via
        the extra `isSupplier` / `isProspect` guards, so the seller branches below
        are untouched fallbacks. viewerRole is mutually exclusive
        (seller|supplier|prospect), so order among the read-only variants is safe.
        Each branch keeps its own distinct :key so a status flip still remounts.
      -->
      <poola-supplier-run
        v-else-if="state === POOLA_STATE.COMING && isSupplier"
        key="srun-scheduled"
        phase="scheduled"
        :deal="deal"
        :server-offset="serverOffset"
        :last-run="lastRun"
        :last-run-href="lastRunHref"
        @deal-not-active="refresh"
      ></poola-supplier-run>

      <poola-prospect-run
        v-else-if="state === POOLA_STATE.COMING && isProspect"
        key="prun-scheduled"
        phase="scheduled"
        :deal="deal"
        :server-offset="serverOffset"
        :last-run="lastRun"
        :last-run-href="lastRunHref"
      ></poola-prospect-run>

      <poola-run
        v-else-if="state === POOLA_STATE.COMING"
        key="run-scheduled"
        phase="scheduled"
        :deal="deal"
        :server-offset="serverOffset"
        :last-run="lastRun"
        :last-run-href="lastRunHref"
        @deal-not-active="refresh"
      ></poola-run>

      <poola-supplier-run
        v-else-if="state === POOLA_STATE.LIVE && isSupplier"
        key="srun-active"
        phase="active"
        :deal="deal"
        :server-offset="serverOffset"
        @deal-not-active="refresh"
      ></poola-supplier-run>

      <poola-prospect-run
        v-else-if="state === POOLA_STATE.LIVE && isProspect"
        key="prun-active"
        phase="active"
        :deal="deal"
        :server-offset="serverOffset"
      ></poola-prospect-run>

      <poola-run
        v-else-if="state === POOLA_STATE.LIVE"
        key="run-active"
        phase="active"
        :deal="deal"
        :server-offset="serverOffset"
        :festival-meta="festival"
        :opened-deal-id="openedDealId"
        @deal-not-active="refresh"
        @totals-changed="refreshFestival"
      ></poola-run>

      <!-- ENDED: the finale card + the viewer's read-only run. The same three
           viewer branches as above, moved into PoolaEndedView so the last-Poola
           page (PoolaLastRunPage) shows exactly the same screen. -->
      <poola-ended-view
        v-else-if="state === POOLA_STATE.ENDED"
        :deal="deal"
        :festival="festival"
        :server-offset="serverOffset"
        :viewer-role="viewerRole"
        @deal-not-active="refresh"
      ></poola-ended-view>
    </f7-page-content>
  </f7-page>
</template>

<script setup>
// Host: derive ONE state from the singleton and render ONE child. No business
// logic beyond the state machine — children own their own data layers
// (PoolaPoolHero → usePoolaTeaser; PoolaRun → usePoolaOffers/useWallets in P5).
import { computed } from 'vue';
import { canBrowseSupplierShell, clubTypeForPath, CLUB_TYPES } from '@/clubs/manifest.js';
import PoolaEndedView from '@/components/poola/PoolaEndedView.vue';
import PoolaError from '@/components/poola/PoolaError.vue';
import PoolaPoolHero from '@/components/poola/PoolaPoolHero.vue';
import PoolaProspectRun from '@/components/poola/PoolaProspectRun.vue';
import PoolaRun from '@/components/poola/PoolaRun.vue';
import PoolaSkeleton from '@/components/poola/PoolaSkeleton.vue';
import PoolaSupplierRun from '@/components/poola/PoolaSupplierRun.vue';
import SupplierTabbarNavigation from '@/components/SupplierTabbarNavigation.vue';
import { POOLA_STATE, usePoolaDeal } from '@/composables/usePoolaDeal.js';
import { useAuthStore } from '@/stores/authStore.js';
import { lastRunWorthShowing } from '@/utils/poolaLastRun.js';
import { poolaPathsFor } from '@/utils/poolaPaths.js';

// F7-Vue passes f7route / f7router as props to `component:` routes. Only the
// route is read here, once, for the recap card's link.
const props = defineProps({
  f7route: { type: Object, default: null },
});

const {
  deal,
  state,
  refresh,
  refreshFestival,
  serverOffset,
  viewerRole,
  festival,
  previousRun,
  openedDealId,
} = usePoolaDeal();

// Viewer routing (BE meta.viewerRole, confirmed 2026-07-10 prospect-open-reads):
//   'supplier' → read-only PoolaSupplierRun (no buy path)
//   'prospect' → read-only PoolaProspectRun (no buy path + sticky «انضم واحجز»)
//   'seller' / anything else → the seller buy PoolaRun (fallback)
// The optional-chain keeps the host crash-safe if a mock/contract omits
// viewerRole — it then defaults to the seller path.
const isSupplier = computed(() => viewerRole?.value === 'supplier');
const isProspect = computed(() => viewerRole?.value === 'prospect');

// The last finished run, for the recap card on the COUNTDOWN screen only (the three
// COMING branches above). The backend sends meta.previousRun only while the current
// run is SCHEDULED, and absent/null means today's screen. A run with nothing to show
// (no numbers, no completed offers) draws no card at all rather than a hollow title
// (backend reply 2026-10-04: a run with no sales is not skipped server-side, so the
// app decides — utils/poolaLastRun.js, the same rule the last-Poola page applies).
// The optional chain keeps the host crash-safe for a mock that omits it.
const lastRun = computed(() => lastRunWorthShowing(previousRun?.value));

// Where the card's «شوف كل عروضها» goes: the last-Poola page of THIS page's
// namespace — /suppliers/poola/last/ under /suppliers/, otherwise /poola/last/ —
// so a supplier stays in the supplier shell's address space through the round
// trip. The route is fixed for a page instance, so this resolves once.
const lastRunHref = computed(
  () => poolaPathsFor(props.f7route?.path ?? window.location.pathname).last,
);

// The supplier tabbar lives in the /suppliers/ shell page, which is not in the
// view on this standalone route — a push replaces it and a cold load never
// mounts it — so /suppliers/poola/ draws the bar itself. Only for a stage that
// may browse the shell (showroom | active): an interested supplier reaches this
// page from his waiting screen, and every shell link would bounce him back.
const authStore = useAuthStore();
const showSupplierBar = computed(
  () =>
    clubTypeForPath(props.f7route?.path ?? window.location.pathname) === CLUB_TYPES.SUPPLIERS &&
    canBrowseSupplierShell({
      supplierStage: authStore.supplierStage,
      isSupplier: authStore.isSupplier,
    }),
);

// PTR → full singleton refetch. usePoolaDeal.refresh resolves `done` in its
// finally, so the F7 pull-to-refresh callback is passed straight through.
function handlePullToRefresh(done) {
  refresh(done);
}
</script>

<style scoped>
/* ── Page background ─────────────────────────────────────────────────────── */
/* Matches the soft gray used by other page shells. LIVE — applies to every
   state the host renders (loading/error/empty/coming/live/ended). */
:deep(.page-content) {
  background: var(--poolit-bg-soft);
}
</style>
